import { Container, Box, Typography, Divider, Stack, Link, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { COMPANY, companyFullAddress } from "../config/companyInfo";

// Srpska verzija je pravno merodavna (usklađena sa ChipCard zahtevima);
// engleska je informativni prevod sa klauzulom o merodavnosti.
export default function TermsPage() {
    const { i18n } = useTranslation();
    const isEn = (i18n.resolvedLanguage || i18n.language || "sr").startsWith("en");
    return isEn ? <TermsEn /> : <TermsSr />;
}

function TermsSr() {
    const navigate = useNavigate();

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "grey.100", py: 4 }}>
            <Container maxWidth="md">
                <Box sx={{ bgcolor: "white", borderRadius: 3, p: { xs: 3, md: 5 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                        Opšti uslovi korišćenja i kupovine karata putem interneta
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Uslovi korišćenja platforme {COMPANY.brandName} za onlajn prodaju autobuskih karata.
                        Molimo Vas da pre kupovine pročitate i{" "}
                        <Link onClick={() => navigate("/politika-privatnosti")} sx={{ cursor: "pointer", fontWeight: 700 }}>
                            Politiku privatnosti
                        </Link>.
                    </Typography>
                    <Divider sx={{ mb: 3 }} />

                    <Section title="1. Osnovni podaci o prodavcu">
                        Prodajno mesto {COMPANY.brandName} ({COMPANY.web}) posluje u okviru privrednog subjekta:
                    </Section>

                    <Box sx={{ bgcolor: "grey.50", borderRadius: 2, p: 2.5, mb: 3 }}>
                        <InfoRow label="Pun naziv pravnog subjekta" value={COMPANY.legalName} />
                        <InfoRow label="Adresa sedišta" value={companyFullAddress} />
                        <InfoRow label="Delatnost i šifra delatnosti" value={`${COMPANY.activity} (${COMPANY.activityCode})`} />
                        <InfoRow label="Matični broj" value={COMPANY.registrationNumber} />
                        <InfoRow label="Poreski broj (PIB)" value={COMPANY.taxId} />
                        <InfoRow label="Web adresa" value={COMPANY.web} />
                        <InfoRow label="Kontakt telefon" value={COMPANY.phone} />
                        <InfoRow label="Kontakt e-mail" value={COMPANY.email} last />
                    </Box>

                    <Section title="2. Opis usluge">
                        Platforma {COMPANY.brandName} omogućava kupovinu autobuskih (voznih) karata putem interneta,
                        za polaske registrovane u redu vožnje. Za svaki polazak prikazani su relacija, datum i vreme
                        polaska, prevoznik i cena karte izražena u dinarima (RSD). Karta se kupcu isporučuje
                        elektronskim putem, u skladu sa članom 6. ovih Uslova.
                    </Section>

                    <Section title="3. Cena i PDV">
                        Sve cene na platformi iskazane su u zvaničnoj valuti Republike Srbije — dinar (RSD),
                        sa uračunatim porezom. Ukupna cena karte, zajedno sa svim eventualnim troškovima,
                        prikazuje se kupcu pre iniciranja plaćanja, u okviru specifikacije narudžbine.
                        {"\n\n"}
                        {COMPANY.isVatPayer
                            ? "PDV je uračunat u prikazanu cenu."
                            : `${COMPANY.legalName} nije u sistemu PDV-a, te PDV nije obračunat i nije iskazan u ceni (u skladu sa članom 33. Zakona o PDV-u).`}
                    </Section>

                    <Section title="4. Izjava o konverziji">
                        Sva plaćanja biće izvršena u lokalnoj valuti Republike Srbije — dinar (RSD). Za informativni
                        prikaz cena u drugim valutama koristi se srednji kurs Narodne banke Srbije. Iznos za koji će
                        biti zadužena Vaša platna kartica biće izražen u Vašoj lokalnoj valuti kroz konverziju u istu
                        po kursu koji koriste kartičarske organizacije, a koji nama u trenutku transakcije ne može biti
                        poznat. Kao rezultat ove konverzije postoji mogućnost neznatne razlike od originalne cene
                        navedene na našem sajtu. Hvala Vam na razumevanju.
                    </Section>

                    <Section title="5. Način i sigurnost plaćanja">
                        Plaćanje se vrši platnim karticama (VISA, Mastercard, Maestro, DinaCard). Podaci o platnoj
                        kartici unose se isključivo na zaštićenoj stranici procesora platnih kartica Chip Card a.d.
                        Beograd i nijednog trenutka nisu dostupni platformi {COMPANY.brandName}. Detaljan opis zaštite
                        poverljivih podataka o transakciji naveden je u{" "}
                        <Link onClick={() => navigate("/politika-privatnosti")} sx={{ cursor: "pointer", fontWeight: 700 }}>
                            Politici privatnosti
                        </Link>.
                    </Section>

                    <Section title="6. Isporuka karte">
                        {COMPANY.brandName} prodaje uslugu prevoza u nematerijalnom (elektronskom) obliku — ne vrši se
                        fizička dostava robe. Nakon uspešno izvršenog plaćanja, kupcu se na e-mail adresu unetu prilikom
                        kupovine dostavljaju autobuska karta i fiskalni račun, po pravilu u roku od nekoliko minuta.
                        {"\n\n"}
                        Molimo Vas da proverite da ste uneli tačnu e-mail adresu i da imate pristup toj adresi. Ukoliko
                        kartu ne dobijete u roku od 30 minuta od plaćanja, kontaktirajte nas putem kontakt podataka iz
                        člana 9. Kupljena karta važi na donosioca; kupac je dužan da je poseduje na svom elektronskom
                        uređaju i po potrebi stavi na uvid osoblju prevoznika ili autobuske stanice. Jedna onlajn
                        kupovina ograničena je na najviše 5 karata.
                    </Section>

                    <Section title="7. Ugovor o prevozu">
                        Prilikom kupovine karte, ugovor o prevozu se zaključuje dostavom karte, odnosno potvrde o
                        uspešno izvršenoj kupovini, na elektronsku adresu korisnika. Kupovinom karte korisnik prihvata
                        uslove prevoza prevoznika i Opšte uslove poslovanja autobuskih stanica.
                        {"\n\n"}
                        Ukoliko je korisnik prilikom kupovine odabrao neki od popusta, prilikom ukrcavanja u autobus je
                        dužan da osoblju prevoznika stavi na uvid dokaz da pripada odabranoj kategoriji popusta. Ukoliko
                        korisnik ne dokaže pravo na popust, dužan je da doplati razliku do pune cene vozne karte.
                    </Section>

                    <Section title="8. Reklamacije, otkaz i povraćaj sredstava">
                        Korisnik ima pravo da odustane od putovanja i podnese zahtev za reklamaciju u skladu sa Opštim
                        uslovima poslovanja autobuskih stanica i važećim propisima.
                        {"\n"}- Ako korisnik odustane od putovanja najkasnije dva časa pre početka putovanja, vrši se
                        povraćaj sredstava za neiskorišćenu kartu, uz zadržavanje 10% od iznosa naplaćene prevoznine.
                        {"\n"}- Zahtev za reklamaciju podnosi se putem kontakt podataka iz člana 9, uz navođenje broja
                        narudžbine i podataka o transakciji.
                        {"\n"}- Na izjavljenu reklamaciju odgovorićemo najkasnije u roku od 8 (osam) dana od dana
                        prijema reklamacije, u skladu sa Zakonom o zaštiti potrošača Republike Srbije.
                        {"\n"}- Povraćaj novca sprovodi se u zakonskom roku, po pravilu do 14 dana od prihvatanja
                        zahteva.
                        {"\n\n"}
                        U slučaju povraćaja sredstava kupcu koji je prethodno platio platnom karticom, delimično ili u
                        celosti, a bez obzira na razlog vraćanja, {COMPANY.legalName} je u obavezi da povraćaj vrši
                        isključivo preko VISA, EC/MC i Maestro metoda plaćanja, odnosno isključivo na račun platne
                        kartice storniranjem originalne transakcije.
                    </Section>

                    <Section title="9. Kontakt podaci">
                        Za sve informacije u vezi sa narudžbinom, reklamacijom ili otkazom, korisnici nam se mogu
                        obratiti:
                        {"\n"}- Adresa: {companyFullAddress}
                        {"\n"}- Telefon: {COMPANY.phone}
                        {"\n"}- E-mail: {COMPANY.email}
                    </Section>

                    <Section title="10. Provera validnosti karte">
                        Korisnik je dužan da na zahtev osoblja prevoznika ili autobuske stanice pokaže voznu kartu radi
                        provere ispravnosti. Vozna karta kupljena na platformi važeća je isključivo za polazak za datum
                        i vreme naznačene na karti.
                        {"\n\n"}
                        Kupovina nakon predviđenog vremena: sa početne stanice linije karta se može kupiti najkasnije
                        do predviđenog vremena polaska. Sa usputne stanice karta se može kupiti i u ograničenom roku
                        (do 20 minuta) nakon predviđenog vremena prolaska autobusa kroz tu stanicu, jer autobus zbog
                        uslova u saobraćaju može da kasni. Takvu kupovinu korisnik vrši isključivo na sopstvenu
                        odgovornost, uz izričitu potvrdu pri kupovini — ukoliko je autobus već prošao stanicu, ta
                        okolnost ne predstavlja osnov za reklamaciju niti povraćaj sredstava.
                    </Section>

                    <Section title="11. Ograničenje odgovornosti">
                        Platforma {COMPANY.brandName} posreduje u prodaji karata i nije odgovorna za:
                        {"\n"}- kašnjenje autobusa u polasku ili dolasku;
                        {"\n"}- udobnost, kvalitet i higijenu vozila;
                        {"\n"}- ponašanje osoblja autobuskog prevoznika;
                        {"\n"}- raspored sedišta u autobusu;
                        {"\n"}- otkazivanje polaska u slučaju više sile;
                        {"\n"}- bezbednost prtljaga putnika;
                        {"\n"}- dodatne troškove proistekle iz opštih uslova poslovanja prevoznika.
                    </Section>

                    <Section title="12. Izmene uslova">
                        {COMPANY.brandName} zadržava pravo izmene ovih Uslova. Važeća verzija uvek je objavljena na ovoj
                        stranici, te je obaveza korisnika da se pre svake kupovine upozna sa aktuelnim Uslovima.
                    </Section>
                </Box>
            </Container>
        </Box>
    );
}

function TermsEn() {
    const navigate = useNavigate();

    return (
        <Box sx={{ minHeight: "calc(100vh - 64px)", bgcolor: "grey.100", py: 4 }}>
            <Container maxWidth="md">
                <Box sx={{ bgcolor: "white", borderRadius: 3, p: { xs: 3, md: 5 } }}>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                        General Terms of Use and Online Ticket Purchase
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Terms of use of the {COMPANY.brandName} platform for online sale of bus tickets.
                        Before purchasing, please also read our{" "}
                        <Link onClick={() => navigate("/politika-privatnosti")} sx={{ cursor: "pointer", fontWeight: 700 }}>
                            Privacy Policy
                        </Link>.
                    </Typography>
                    <Alert severity="info" sx={{ mb: 3 }}>
                        This English translation is provided for convenience only. In case of any
                        discrepancy, the Serbian version prevails.
                    </Alert>
                    <Divider sx={{ mb: 3 }} />

                    <Section title="1. Seller information">
                        The {COMPANY.brandName} point of sale ({COMPANY.web}) operates within the following legal entity:
                    </Section>

                    <Box sx={{ bgcolor: "grey.50", borderRadius: 2, p: 2.5, mb: 3 }}>
                        <InfoRow label="Full legal name" value={COMPANY.legalName} />
                        <InfoRow label="Registered address" value={companyFullAddress} />
                        <InfoRow label="Activity and activity code" value={`${COMPANY.activity} (${COMPANY.activityCode})`} />
                        <InfoRow label="Company registration number" value={COMPANY.registrationNumber} />
                        <InfoRow label="Tax identification number (PIB)" value={COMPANY.taxId} />
                        <InfoRow label="Website" value={COMPANY.web} />
                        <InfoRow label="Contact phone" value={COMPANY.phone} />
                        <InfoRow label="Contact e-mail" value={COMPANY.email} last />
                    </Box>

                    <Section title="2. Description of the service">
                        The {COMPANY.brandName} platform enables the online purchase of bus tickets for departures
                        registered in the official timetable. For each departure, the route, date and time of
                        departure, carrier and ticket price in Serbian dinars (RSD) are displayed. The ticket is
                        delivered to the buyer electronically, in accordance with Article 6 of these Terms.
                    </Section>

                    <Section title="3. Price and VAT">
                        All prices on the platform are expressed in the official currency of the Republic of Serbia —
                        the dinar (RSD), with tax included. The total ticket price, together with any additional
                        costs, is shown to the buyer before payment is initiated, within the order summary.
                        {"\n\n"}
                        {COMPANY.isVatPayer
                            ? "VAT is included in the displayed price."
                            : `${COMPANY.legalName} is not registered in the VAT system, therefore VAT is not calculated and not shown in the price (in accordance with Article 33 of the Serbian VAT Act).`}
                    </Section>

                    <Section title="4. Currency conversion statement">
                        All payments will be effected in the local currency of the Republic of Serbia — dinar (RSD).
                        For informational display of prices in other currencies, the official middle exchange rate of
                        the National Bank of Serbia is used. The amount your payment card will be charged for will be
                        expressed in your local currency through conversion at the rate used by card organisations,
                        which cannot be known to us at the moment of the transaction. As a result of this conversion,
                        a slight difference from the original price stated on our website is possible. Thank you for
                        your understanding.
                    </Section>

                    <Section title="5. Payment method and security">
                        Payment is made with payment cards (VISA, Mastercard, Maestro, DinaCard). Payment card details
                        are entered exclusively on the protected page of the payment card processor Chip Card a.d.
                        Belgrade and are at no point accessible to the {COMPANY.brandName} platform. A detailed
                        description of the protection of confidential transaction data is provided in the{" "}
                        <Link onClick={() => navigate("/politika-privatnosti")} sx={{ cursor: "pointer", fontWeight: 700 }}>
                            Privacy Policy
                        </Link>.
                    </Section>

                    <Section title="6. Ticket delivery">
                        {COMPANY.brandName} sells the transport service in intangible (electronic) form — no physical
                        delivery of goods takes place. After successful payment, the bus ticket and the fiscal receipt
                        are delivered to the e-mail address entered during purchase, as a rule within a few minutes.
                        {"\n\n"}
                        Please make sure you have entered a correct e-mail address and that you have access to it. If
                        you do not receive the ticket within 30 minutes of payment, contact us using the contact
                        details in Article 9. The purchased ticket is valid for the bearer; the buyer must keep it on
                        an electronic device and present it to the staff of the carrier or bus station upon request.
                        A single online purchase is limited to a maximum of 5 tickets.
                    </Section>

                    <Section title="7. Contract of carriage">
                        When a ticket is purchased, the contract of carriage is concluded upon delivery of the ticket,
                        i.e. the purchase confirmation, to the user's e-mail address. By purchasing a ticket, the user
                        accepts the carrier's conditions of carriage and the General Terms of Business of bus stations.
                        {"\n\n"}
                        If the user selected a discount during purchase, when boarding the bus they must present proof
                        of belonging to the selected discount category to the carrier's staff. If the user cannot prove
                        the right to the discount, they must pay the difference up to the full ticket price.
                    </Section>

                    <Section title="8. Complaints, cancellation and refunds">
                        The user has the right to cancel the journey and submit a complaint in accordance with the
                        General Terms of Business of bus stations and applicable regulations.
                        {"\n"}- If the user cancels the journey no later than two hours before its start, the unused
                        ticket is refunded, with 10% of the charged fare being retained.
                        {"\n"}- A complaint is submitted using the contact details in Article 9, stating the order
                        number and transaction details.
                        {"\n"}- We will respond to a submitted complaint within 8 (eight) days of its receipt at the
                        latest, in accordance with the Consumer Protection Act of the Republic of Serbia.
                        {"\n"}- Refunds are carried out within the statutory period, as a rule within 14 days of the
                        acceptance of the request.
                        {"\n\n"}
                        In the event of a refund to a buyer who previously paid with a payment card, partially or in
                        full, and regardless of the reason for the refund, {COMPANY.legalName} is obliged to make the
                        refund exclusively via VISA, EC/MC and Maestro payment methods, i.e. exclusively to the account
                        of the payment card by reversing the original transaction.
                    </Section>

                    <Section title="9. Contact details">
                        For all information regarding an order, complaint or cancellation, users can contact us:
                        {"\n"}- Address: {companyFullAddress}
                        {"\n"}- Phone: {COMPANY.phone}
                        {"\n"}- E-mail: {COMPANY.email}
                    </Section>

                    <Section title="10. Ticket validity check">
                        The user must present the ticket for inspection at the request of the staff of the carrier or
                        bus station. A ticket purchased on the platform is valid exclusively for the departure on the
                        date and at the time indicated on the ticket.
                        {"\n\n"}
                        Purchase after the scheduled time: from the first station of the line, a ticket can be
                        purchased no later than the scheduled departure time. From an intermediate station, a ticket
                        can also be purchased within a limited period (up to 20 minutes) after the scheduled time the
                        bus passes through that station, as the bus may be delayed due to traffic conditions. Such a
                        purchase is made exclusively at the user's own risk, with explicit confirmation during
                        purchase — if the bus has already passed the station, this circumstance does not constitute
                        grounds for a complaint or refund.
                    </Section>

                    <Section title="11. Limitation of liability">
                        The {COMPANY.brandName} platform acts as an intermediary in ticket sales and is not responsible
                        for:
                        {"\n"}- bus delays at departure or arrival;
                        {"\n"}- comfort, quality and hygiene of the vehicle;
                        {"\n"}- conduct of the bus carrier's staff;
                        {"\n"}- seating arrangement on the bus;
                        {"\n"}- cancellation of the departure in cases of force majeure;
                        {"\n"}- safety of passengers' luggage;
                        {"\n"}- additional costs arising from the carrier's general terms of business.
                    </Section>

                    <Section title="12. Changes to the Terms">
                        {COMPANY.brandName} reserves the right to amend these Terms. The applicable version is always
                        published on this page, and the user is obliged to review the current Terms before each
                        purchase.
                    </Section>
                </Box>
            </Container>
        </Box>
    );
}

function Section({ title, children }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "primary.main" }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8, whiteSpace: "pre-line" }}>
                {children}
            </Typography>
        </Box>
    );
}

function InfoRow({ label, value, last }) {
    return (
        <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            sx={{ py: 1, borderBottom: last ? "none" : "1px solid", borderColor: "grey.200" }}
        >
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {label}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, textAlign: { sm: "right" } }}>
                {value}
            </Typography>
        </Stack>
    );
}
